import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { useSeo } from "@/hooks/useSeo";
import { Header, scrollToId } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Manifesto } from "@/components/Manifesto";
import { Fees } from "@/components/Fees";
import { Services } from "@/components/Services";
import { About } from "@/components/About";
import { Locations } from "@/components/Locations";
import { Testimonials } from "@/components/Testimonials";
import { TopBanner } from "@/components/ComingSoon";
import { MobileActionBar } from "@/components/MobileActionBar";
import { EnquiryForm } from "@/components/EnquiryForm";
import { Footer } from "@/components/Footer";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import LocationPage from "@/pages/LocationPage";
import ServicePage from "@/pages/ServicePage";
import FeeHealthCheckPage from "@/pages/FeeHealthCheckPage";
import ClientUploadPage from "@/pages/ClientUploadPage";
import FactFindPage from "@/pages/FactFindPage";
import OnlineAdvicePage from "@/pages/OnlineAdvicePage";

const Landing = () => {
  useSeo({ path: "/" });

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const timer = setTimeout(() => scrollToId(id), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <>
      <TopBanner />
      <Header topOffset="top-8 md:top-10" />
      <main>
        <Hero />
        <Manifesto />
        <Fees />
        <Services />
        <About />
        <Testimonials />
        <Locations />
        <EnquiryForm />
      </main>
      <Footer />
      <MobileActionBar />
    </>
  );
};

const Page = ({ children }) => {
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return <>{children}</>;
  }
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      {children}
    </motion.div>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    // GA4 page_view on initial load and every client-side route change.
    const t = setTimeout(() => {
      if (typeof window.gtag === "function") {
        const params = new URLSearchParams(location.search);
        params.delete("resume");
        const qs = params.toString() ? `?${params}` : "";
        window.gtag("event", "page_view", {
          page_path: location.pathname + qs,
          page_location: window.location.origin + location.pathname + qs,
          page_title: document.title,
        });
      }
    }, 0);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Page><Landing /></Page>} />
        <Route path="/locations/:slug" element={<Page><LocationPage /></Page>} />
        <Route path="/services/aged-care-centrelink-advice" element={<Navigate to="/services/centrelink-advice" replace />} />
        <Route path="/services/:slug" element={<Page><ServicePage /></Page>} />
        <Route path="/advice-fee-health-check" element={<Page><FeeHealthCheckPage /></Page>} />
        <Route path="/client-documents" element={<Page><ClientUploadPage /></Page>} />
        <Route path="/online-advice" element={<Page><OnlineAdvicePage /></Page>} />
        <Route path="/fact-find" element={<Page><FactFindPage /></Page>} />
        <Route path="/canberra" element={<Navigate to="/locations/canberra" replace />} />
        <Route path="/sydney" element={<Navigate to="/locations/sydney" replace />} />
        <Route path="/albury" element={<Navigate to="/locations/albury" replace />} />
        <Route path="/nsw-south-coast" element={<Navigate to="/locations/nsw-south-coast" replace />} />
        <Route path="/south-coast" element={<Navigate to="/locations/nsw-south-coast" replace />} />
        <Route path="/admin/login" element={<Page><AdminLogin /></Page>} />
        <Route path="/admin" element={<Page><AdminDashboard /></Page>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  useEffect(() => {
    const splash = document.getElementById("app-splash");
    if (!splash) return;
    let done = false;
    const hide = () => {
      if (done) return;
      done = true;
      splash.classList.add("app-splash--hidden");
      setTimeout(() => splash.remove(), 600);
    };
    const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve();
    // Reveal once the theme + fonts are ready, capped so it always feels fast.
    Promise.race([fontsReady, new Promise((r) => setTimeout(r, 1200))]).then(() =>
      requestAnimationFrame(() => requestAnimationFrame(hide))
    );
    return () => {
      done = true;
    };
  }, []);

  return (
    <div className="App">
      <div className="noise-overlay" aria-hidden="true" />
      <BrowserRouter>
        <AnimatedRoutes />
      </BrowserRouter>
      <Toaster position="bottom-right" mobileOffset={{ bottom: 80 }} />
    </div>
  );
}

export default App;
